import type { AssignedUser, Priority, PriorityAssignment, PriorityPppRef, PriorityStatus } from "@/types";

function parsePriorityPppRef(raw: unknown): PriorityPppRef {
  const r = raw as PriorityPppRef;
  return {
    ...r,
    isConfidential: r.isConfidential ?? false,
  };
}

function parseAssignedUser(raw: unknown): AssignedUser | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const u = raw as Record<string, unknown>;
  if (typeof u.pid !== "string" || typeof u.fullName !== "string") return undefined;
  return {
    pid: u.pid,
    fullName: u.fullName,
    avatarUrl: typeof u.avatarUrl === "string" ? u.avatarUrl : undefined,
  };
}

function parseDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * Normalizes a single assignment from either the flat BE shape (`userPid`/`userFullName`)
 * or the nested legacy shape (`assignedUser`). Returns null for unrecognised shapes.
 */
export function parsePriorityAssignment(raw: unknown): PriorityAssignment | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;

  const nested = parseAssignedUser(a.assignedUser);
  if (nested) {
    return {
      assignedUser: nested,
      assignedAt: parseDate(a.assignedAt),
      status: (a.status as PriorityStatus | null | undefined) ?? undefined,
      icTask: (a.icTask as string | null | undefined) ?? null,
      isDri: typeof a.isDri === "boolean" ? a.isDri : undefined,
      blocker: parseAssignedUser(a.blocker),
      blockedReason: (a.blockedReason as string | null | undefined) ?? null,
    };
  }

  const userPid = a.userPid;
  if (typeof userPid !== "string") return null;

  const fullName =
    typeof a.userFullName === "string"
      ? a.userFullName
      : typeof a.fullName === "string"
        ? a.fullName
        : "";

  return {
    assignedUser: { pid: userPid, fullName },
    assignedAt: parseDate(a.assignedAt),
    status: (a.status as PriorityStatus | null | undefined) ?? undefined,
    icTask: (a.icTask as string | null | undefined) ?? null,
    isDri: typeof a.isDri === "boolean" ? a.isDri : undefined,
    blocker: parseAssignedUser(a.blocker),
    blockedReason: (a.blockedReason as string | null | undefined) ?? null,
  };
}

/**
 * Normalizes the `assignments` array on a raw priority API response.
 * All other top-level fields are passed through as-is from the API response —
 * the caller is responsible for ensuring the BE returns the expected shape.
 * Throws if `raw` is not an object.
 */
export function parsePriority(raw: unknown): Priority {
  if (raw === null || typeof raw !== "object") {
    throw new Error(`parsePriority: expected object, got ${raw === null ? "null" : typeof raw}`);
  }
  const p = raw as Priority;
  const assignmentsRaw = (raw as Record<string, unknown>).assignments;
  const assignments = Array.isArray(assignmentsRaw)
    ? (assignmentsRaw.map(parsePriorityAssignment).filter(Boolean) as PriorityAssignment[])
    : [];

  const pppsRaw = (raw as Record<string, unknown>).ppps;
  return {
    ...p,
    assignments,
    ppps: Array.isArray(pppsRaw) ? pppsRaw.map(parsePriorityPppRef) : [],
    isCandidate: (raw as Record<string, unknown>).isCandidate === true,
    inheritsConfidential: (raw as Record<string, unknown>).inheritsConfidential === true,
  };
}
